import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { format, parseISO } from "date-fns";
import { QUEUE_COLORS } from "./pipelineConfig";
import { isLambdaStage } from "./layout";

export const RESOLUTION_OPTIONS = [
  { value: 1, label: "1-minute groups" },
  { value: 5, label: "5-minute groups" },
  { value: 15, label: "15-minute groups" },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const timeLabel = label
    ? format(parseISO(label), "MMM d, HH:mm") + " UTC"
    : "";

  const entries = payload
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value);

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm max-w-xs">
      <p className="font-semibold text-gray-800 mb-2">{timeLabel}</p>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {entries.map((entry) => (
          <p key={entry.dataKey} className="flex justify-between gap-4">
            <span style={{ color: entry.color }}>{entry.name}</span>
            <span className="font-medium">{entry.value}</span>
          </p>
        ))}
        {entries.length === 0 && (
          <p className="text-gray-500">No activity in this bucket</p>
        )}
      </div>
    </div>
  );
};

function buildRawChartData(metrics, stages) {
  if (!stages.length) return [];

  const timestampSet = new Set();
  for (const stage of stages) {
    if (isLambdaStage(stage)) {
      const points = metrics?.lambdas?.[stage.functionName]?.points ?? [];
      for (const p of points) {
        timestampSet.add(p.timestamp);
      }
      continue;
    }

    const points = metrics?.queues?.[stage.queue]?.points ?? [];
    for (const p of points) {
      timestampSet.add(p.timestamp);
    }
  }

  return Array.from(timestampSet)
    .sort()
    .map((timestamp) => {
      const row = { timestamp };
      for (const stage of stages) {
        if (isLambdaStage(stage)) {
          const points = metrics?.lambdas?.[stage.functionName]?.points ?? [];
          const point = points.find((p) => p.timestamp === timestamp);
          row[`${stage.key}_invocations`] = point?.invocations ?? 0;
          continue;
        }

        const points = metrics?.queues?.[stage.queue]?.points ?? [];
        const point = points.find((p) => p.timestamp === timestamp);
        row[`${stage.key}_sent`] = point?.sent ?? 0;
        row[`${stage.key}_deleted`] = point?.deleted ?? 0;
      }
      return row;
    });
}

function downsampleRawChartData(chartData, stages, bucketMinutes) {
  if (bucketMinutes <= 1 || chartData.length === 0) return chartData;

  const bucketMs = bucketMinutes * 60 * 1000;
  const buckets = new Map();

  for (const row of chartData) {
    const ts = parseISO(row.timestamp).getTime();
    const bucketStart = Math.floor(ts / bucketMs) * bucketMs;

    if (!buckets.has(bucketStart)) {
      const bucketRow = { timestamp: new Date(bucketStart).toISOString() };
      for (const stage of stages) {
        if (isLambdaStage(stage)) {
          bucketRow[`${stage.key}_invocations`] = 0;
        } else {
          bucketRow[`${stage.key}_sent`] = 0;
          bucketRow[`${stage.key}_deleted`] = 0;
        }
      }
      buckets.set(bucketStart, bucketRow);
    }

    const bucketRow = buckets.get(bucketStart);
    for (const stage of stages) {
      if (isLambdaStage(stage)) {
        bucketRow[`${stage.key}_invocations`] +=
          row[`${stage.key}_invocations`] ?? 0;
      } else {
        bucketRow[`${stage.key}_sent`] += row[`${stage.key}_sent`] ?? 0;
        bucketRow[`${stage.key}_deleted`] += row[`${stage.key}_deleted`] ?? 0;
      }
    }
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row);
}

function computeYDomain(chartData, visibleStages) {
  let max = 0;
  for (const row of chartData) {
    for (const stage of visibleStages) {
      if (isLambdaStage(stage)) {
        max = Math.max(max, row[`${stage.key}_invocations`] ?? 0);
      } else {
        max = Math.max(
          max,
          row[`${stage.key}_sent`] ?? 0,
          row[`${stage.key}_deleted`] ?? 0
        );
      }
    }
  }
  if (max === 0) return [0, 5];
  return [0, Math.max(max + 1, Math.ceil(max * 1.15))];
}

const chartMargin = { top: 10, right: 30, left: 10, bottom: 5 };

const ThroughputChart = ({
  pipeline,
  metrics,
  selectedStageKey,
  hours,
  resolutionMinutes = 5,
  selectedTimestamp,
}) => {
  const stages = pipeline?.stages ?? [];

  const visibleStages = useMemo(() => {
    if (selectedStageKey) {
      return stages.filter((s) => s.key === selectedStageKey);
    }
    return stages;
  }, [stages, selectedStageKey]);

  const chartData = useMemo(() => {
    const raw = buildRawChartData(metrics, stages);
    return downsampleRawChartData(raw, stages, resolutionMinutes);
  }, [metrics, stages, resolutionMinutes]);

  const referenceTimestamp = useMemo(() => {
    if (!selectedTimestamp || !chartData.length) return null;
    if (chartData.some((row) => row.timestamp === selectedTimestamp)) {
      return selectedTimestamp;
    }

    const target = parseISO(selectedTimestamp).getTime();
    let best = chartData[0].timestamp;
    let bestDiff = Infinity;

    for (const row of chartData) {
      const diff = Math.abs(parseISO(row.timestamp).getTime() - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = row.timestamp;
      }
    }

    return best;
  }, [selectedTimestamp, chartData]);

  const yDomain = useMemo(
    () => computeYDomain(chartData, visibleStages),
    [chartData, visibleStages]
  );

  const formatXAxis = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return hours > 24 ? format(date, "MMM d HH:mm") : format(date, "HH:mm");
    } catch {
      return timestamp;
    }
  };

  const axisProps = {
    stroke: "#6b7280",
    fontSize: 11,
    tickLine: false,
  };

  if (!metrics) {
    return (
      <div className="h-[360px] flex items-center justify-center text-gray-500">
        Loading metrics…
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className="h-[360px] flex items-center justify-center text-gray-500">
        No throughput data for the selected window.
      </div>
    );
  }

  const isSingleStage = visibleStages.length === 1;
  const hasLambdaSelected = visibleStages.some(isLambdaStage);
  const yAxisLabel = hasLambdaSelected && isSingleStage
    ? "Invocations / bucket"
    : "Messages / bucket";

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={chartMargin}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="timestamp"
            tickFormatter={formatXAxis}
            {...axisProps}
            minTickGap={50}
            axisLine={false}
          />
          <YAxis
            {...axisProps}
            axisLine={false}
            allowDecimals={false}
            domain={yDomain}
            label={{
              value: yAxisLabel,
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#6b7280" },
            }}
          />
          <Tooltip content={<ChartTooltip />} />
          {referenceTimestamp && (
            <ReferenceLine
              x={referenceTimestamp}
              stroke="#783BFF"
              strokeWidth={2}
              strokeDasharray="4 4"
              label={{
                value: "Scrubber",
                position: "insideTopRight",
                fill: "#783BFF",
                fontSize: 11,
              }}
            />
          )}
          <Legend wrapperStyle={{ paddingTop: 12 }} iconType="line" />
          {visibleStages.map((stage) => {
            const stageIndex = stages.findIndex((s) => s.key === stage.key);
            const color = QUEUE_COLORS[stageIndex % QUEUE_COLORS.length];
            const stageLabel = stage.shortName;
            const emphasize = isSingleStage || selectedStageKey === stage.key;

            if (isLambdaStage(stage)) {
              return (
                <Line
                  key={stage.key}
                  type="monotone"
                  dataKey={`${stage.key}_invocations`}
                  name={`${stageLabel} (invocations)`}
                  stroke={color}
                  strokeWidth={emphasize ? 2.5 : 2}
                  dot={false}
                  isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              );
            }

            return (
              <React.Fragment key={stage.key}>
                <Line
                  type="monotone"
                  dataKey={`${stage.key}_deleted`}
                  name={`${stageLabel} (deleted)`}
                  stroke={color}
                  strokeWidth={emphasize ? 2.5 : 2}
                  dot={false}
                  isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
                <Line
                  type="monotone"
                  dataKey={`${stage.key}_sent`}
                  name={`${stageLabel} (sent)`}
                  stroke={color}
                  strokeWidth={emphasize ? 2 : 1.5}
                  strokeDasharray="6 4"
                  strokeOpacity={0.85}
                  dot={false}
                  isAnimationActive={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                  legendType="none"
                />
              </React.Fragment>
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ThroughputChart;
